import type { MFEDependencies } from "@pepperstone/mfe-dev-kit/types";

/**
 * Payment error tracking utilities using WebX Datadog logger
 *
 * IMPORTANT: Only logs ERRORS and CRITICAL ISSUES to avoid noise in Datadog.
 * Success events are tracked via backend metrics, not frontend logging.
 */

type LoggerInstance = Console | MFEDependencies["logger"];

export const createPaymentTracker = (logger: LoggerInstance) => {
  return {
    /**
     * Track deposit errors only
     */
    deposit: {
      /**
       * Log deposit failure - CRITICAL
       */
      failed: (context: {
        methodId: string;
        amount?: number;
        error: string;
        errorCode?: string;
      }) => {
        logger.error("Deposit failed", {
          event: "deposit_failed",
          ...context,
        });
      },

      /**
       * Log when the customer explicitly requests cancellation of an
       * in-flight deposit (e.g. by closing the PSP iframe or hitting Back on
       * the manual-confirm modal). Fired at the moment of the user action —
       * the transaction may or may not actually end up in Cancelled status
       * (a PSP webhook could race in). Use `deposit.cancelled` for the
       * terminal-state signal.
       */
      cancellationRequested: (context: {
        methodId?: string;
        referenceId?: string;
        step?: string;
      }) => {
        logger.warn("Deposit cancellation requested", {
          event: "deposit_cancellation_requested",
          ...context,
        });
      },

      /**
       * Log when a deposit transaction is observed to be in the terminal
       * Cancelled state (typically from the Thank You Page after polling).
       * Distinct from `cancellationRequested` so dashboards can compare how
       * many user-initiated cancellations actually resolved as Cancelled.
       */
      cancelled: (context: {
        methodId?: string;
        referenceId?: string;
        step?: string;
      }) => {
        logger.warn("Deposit cancelled", {
          event: "deposit_cancelled",
          ...context,
        });
      },
    },

    /**
     * Track withdrawal errors only
     */
    withdrawal: {
      /**
       * Log withdrawal failure - CRITICAL
       */
      failed: (context: {
        methodId: string;
        amount?: number;
        error: string;
        errorCode?: string;
      }) => {
        logger.error("Withdrawal failed", {
          event: "withdrawal_failed",
          ...context,
        });
      },
    },
  };
};
